import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  selectCurrency,
  selectSubscriptionCurrentPrice,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialPeriodPrice,
} from 'modules/purchase/redux/selects'
import { CENTS_IN_DOLLAR } from 'root-constants'
import {
  purchaseAction,
  CHECK_PAYMENT_REQUEST_BUTTON,
} from 'modules/purchase/redux/actions'
import { useLocation } from 'react-router'
import { getPageIdFromPathName } from 'helpers/getPageIdFromPathName'
import { resetErrorAction, stopFetching } from 'root-redux/actions/common'

import { Separator } from 'modules/purchase/components/Separator'
import { TPaymentRequestButton } from 'modules/purchase/types'
import { PaymentMethod } from 'modules/purchase/constants'
import { eventLogger } from 'services/eventLogger.service'
import { PaymentRequest } from '@stripe/stripe-js/types/stripe-js/payment-request'
import { StyledPaymentRequestButton as S } from './PaymentRequestButton.styles'

export const PaymentRequestButton: React.FC = () => {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  const trialCurrentPrice = useSelector(selectSubscriptionTrialPeriodPrice)
  const periodName = useSelector(selectSubscriptionPeriodName)
  const periodQuantity = useSelector(selectSubscriptionPeriodQuantity)
  const selectedPrice = useSelector(selectSubscriptionCurrentPrice)
  const currency = useSelector(selectCurrency)

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  )
  const [buttonTypes, setButtonTypes] = useState<TPaymentRequestButton | null>(
    null,
  )

  const planPeriodDescription = useMemo(
    () =>
      t('purchase.checkout.planDescription', {
        periodQuantity,
        periodName,
      }),
    [t, periodName, periodQuantity],
  )

  const calculatedPrice = useMemo(
    () => +((trialCurrentPrice || selectedPrice) * CENTS_IN_DOLLAR).toFixed(),
    [trialCurrentPrice, selectedPrice],
  )

  const currentPageId = useMemo(
    () => getPageIdFromPathName(pathname),
    [pathname],
  )

  useEffect(() => {
    if (!stripe || !elements) {
      return
    }

    const pr = stripe?.paymentRequest({
      currency,
      country: 'GB',
      requestPayerEmail: true,
      requestPayerName: true,
      total: {
        label: planPeriodDescription,
        amount: calculatedPrice,
      },
    })

    pr?.canMakePayment().then((result) => {
      if (result) {
        setButtonTypes(result as TPaymentRequestButton)
        setPaymentRequest(pr)
      }
      dispatch(stopFetching(CHECK_PAYMENT_REQUEST_BUTTON))
    })

    pr?.on('paymentmethod', (event) => {
      dispatch(resetErrorAction())
      dispatch(
        purchaseAction({
          stripe,
          paymentPageId: currentPageId,
          createPaymentResFromDigitalWallet: event,
        }),
      )
    })
  }, [
    dispatch,
    currency,
    stripe,
    elements,
    planPeriodDescription,
    calculatedPrice,
    currentPageId,
  ])

  const handleButtonClick = useCallback(() => {
    const shownButtonType = buttonTypes?.applePay
      ? PaymentMethod.APPLE_PAY
      : PaymentMethod.GOOGLE_PAY

    eventLogger.logPaymentMethodSelected(shownButtonType)
  }, [buttonTypes])

  if (paymentRequest) {
    return (
      <S.Wrapper>
        <Separator />
        <PaymentRequestButtonElement
          onClick={handleButtonClick}
          options={{
            paymentRequest,
            style: {
              paymentRequestButton: {
                height: '56px',
                theme: 'light',
              },
            },
          }}
        />
      </S.Wrapper>
    )
  }

  return null
}
